import { MessageSquare, Search, FileText, CheckCircle } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Initial Legal Consultation",
    description: "We begin with a detailed consultation to understand the proposed investment and identify any legal risks. Our firm advises on the safest structure for acquiring and holding property in Nigeria."
  },
  {
    number: "02",
    icon: Search,
    title: "Independent Title Verification",
    description: "Our firm conducts a comprehensive legal investigation of the property title, land registry records, ownership history, and any encumbrances to ensure the property is legally valid and transferable."
  },
  {
    number: "03",
    icon: FileText,
    title: "Transaction Structuring",
    description: "We prepare, review, and negotiate all transaction documentation to ensure the acquisition complies with Nigerian property law and protects the client's interests."
  },
  {
    number: "04",
    icon: CheckCircle,
    title: "Secure Completion and Registration",
    description: "Our firm supervises the completion process, ensures payments are legally structured, and oversees registration of the property with the appropriate land registry authorities so the transaction is completed safely and lawfully."
  }
];

export const InvestmentProcessSection = () => {
  return (
    <section 
      className="py-16 md:py-24 lg:py-32 bg-[#FAFAF9] relative overflow-hidden"
      data-testid="investment-process-section"
    >
      {/* Decorative top line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            How We Work
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-[#0A0A0B] mt-4 md:mt-6 leading-tight">
            Our Secure Property
            <span className="block text-[#D4AF37] italic">Investment Process</span>
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
          
          {/* Introduction text */}
          <p className="max-w-3xl mx-auto mt-8 md:mt-10 text-[#0A0A0B]/60 text-sm md:text-base lg:text-lg leading-relaxed">
            Property investment in Nigeria requires careful legal oversight to avoid defective titles, 
            ownership disputes, and fraudulent transactions. Our firm follows a structured legal process 
            designed to protect both local and diaspora investors.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="group relative p-6 md:p-8 bg-white border border-[#0A0A0B]/10 hover:border-[#D4AF37]/50 hover:shadow-lg transition-all duration-300"
              data-testid={`process-step-${index}`}
            >
              {/* Step number badge */}
              <div className="absolute -top-3 -left-3 md:-top-4 md:-left-4 w-10 h-10 md:w-12 md:h-12 bg-[#D4AF37] flex items-center justify-center">
                <span className="text-[#0A0A0B] font-bold text-sm md:text-base">{step.number}</span>
              </div>
              
              <div className="flex items-start gap-4 md:gap-5 pt-2">
                <div className="w-12 h-12 md:w-14 md:h-14 border border-[#D4AF37]/30 bg-[#0A0A0B] flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37] transition-all duration-300">
                  <step.icon className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37] group-hover:text-[#0A0A0B] transition-colors duration-300" />
                </div>
                <div className="flex-1">
                  <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-[#0A0A0B] mb-2 md:mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#0A0A0B]/60 text-sm md:text-base leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Highlighted statement */}
        <div className="mt-12 md:mt-16 lg:mt-20">
          <div className="relative bg-[#0A0A0B] p-6 md:p-8 lg:p-10 text-center">
            {/* Gold corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 md:w-12 md:h-12 border-l-2 border-t-2 border-[#D4AF37]" />
            <div className="absolute bottom-0 right-0 w-8 h-8 md:w-12 md:h-12 border-r-2 border-b-2 border-[#D4AF37]" />
            
            <p className="font-['Playfair_Display'] text-lg md:text-xl lg:text-2xl text-white italic leading-relaxed max-w-3xl mx-auto">
              "Every property transaction handled by our firm undergoes independent legal verification before completion."
            </p>
          </div>
        </div>
      </div>
      
      {/* Decorative bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
    </section>
  );
};
